import React, { useState } from 'react';
import { Form, Input, Button, Space, Modal, message, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { Delivery, DeliveryRequest } from '../../types/delivery';
import { deliveryService } from '../../services/deliveryService';

interface DeliveryFormProps {
  delivery?: Delivery;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DeliveryForm: React.FC<DeliveryFormProps> = ({
  delivery,
  isVisible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const isEditing = !!delivery;
  const title = isEditing 
    ? t('deliveries.form.edit_title') 
    : t('deliveries.form.create_title');

  const handleSubmit = async (values: DeliveryRequest) => {
    setIsLoading(true);
    try {
      if (isEditing && delivery) {
        await deliveryService.updateDelivery(delivery.id, values);
        message.success(t('deliveries.notifications.update_success'));
      } else {
        await deliveryService.createDelivery(values);
        message.success(t('deliveries.notifications.create_success'));
      }
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error submitting delivery form:', error);
      message.error(t('deliveries.notifications.error', { message: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form when modal opens or delivery changes
  React.useEffect(() => {
    if (isVisible) {
      form.setFieldsValue({
        name: delivery?.name || '',
        domain: delivery?.domain || '',
        is_default: delivery?.is_default || false,
      });
    }
  }, [form, delivery, isVisible]);

  return (
    <Modal
      title={title}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          name: delivery?.name || '',
          domain: delivery?.domain || '',
          is_default: delivery?.is_default || false,
        }}
      >
        <Form.Item
          name="name"
          label={t('deliveries.form.name')}
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Input placeholder={t('deliveries.form.name_placeholder')} />
        </Form.Item>

        <Form.Item
          name="domain"
          label={t('deliveries.form.domain')}
          rules={[
            {
              required: true,
              message: t('deliveries.form.domain_required'),
            },
          ]}
        >
          <Input placeholder={t('deliveries.form.domain_placeholder')} />
        </Form.Item>

        <Form.Item
          name="is_default"
          label={t('deliveries.form.is_default')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
            >
              {t('deliveries.form.submit')}
            </Button>
            <Button onClick={onClose}>
              {t('deliveries.form.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DeliveryForm; 